import * as React from "react";

/**
 * Rect compatible with Base UI `Positioner` `collisionBoundary` (Floating UI `Rect`).
 * Keeps collision detection aligned with the **visual viewport** (iOS keyboard shrinks this),
 * not only the layout viewport — so `side: bottom` lists get correct `--available-height` and
 * stay below the anchor instead of flipping upward.
 */
export type VisualViewportCollisionRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function readVisualViewportRect(): VisualViewportCollisionRect {
  if (typeof window === "undefined") {
    return {x: 0, y: 0, width: 0, height: 0};
  }
  const vv = window.visualViewport;
  if (vv) {
    return {
      x: vv.offsetLeft,
      y: vv.offsetTop,
      width: vv.width,
      height: vv.height,
    };
  }
  return {x: 0, y: 0, width: window.innerWidth, height: window.innerHeight};
}

function areRectsEqual(
  a: VisualViewportCollisionRect | "clipping-ancestors",
  b: VisualViewportCollisionRect | "clipping-ancestors",
): boolean {
  if (a === "clipping-ancestors" || b === "clipping-ancestors") return a === b;
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

/**
 * The soft keyboard animates the visual viewport over many frames, firing `resize`/`scroll`
 * continuously. Repositioning the combobox popup on every tick moves items under the finger
 * between `pointerdown` and `click`, which cancels the tap so no selection commits (item
 * highlights but never gets a checkmark). Waiting for the viewport to settle repositions once
 * the animation stops instead of mid-gesture.
 */
const VIEWPORT_SETTLE_MS = 150;

/**
 * Returns a collision boundary rect that tracks `visualViewport` resize/scroll (keyboard),
 * updated after the viewport settles so the popup does not shift under an in-flight tap.
 * Falls back to `clipping-ancestors` only before mount (SSR / first paint).
 */
export function useVisualViewportCollisionRect():
  | VisualViewportCollisionRect
  | "clipping-ancestors" {
  const [boundary, setBoundary] = React.useState<
    VisualViewportCollisionRect | "clipping-ancestors"
  >(() => (typeof window !== "undefined" ? readVisualViewportRect() : "clipping-ancestors"));

  React.useEffect(() => {
    let settleTimeout = 0;

    function commit() {
      setBoundary((prev) => {
        const next = readVisualViewportRect();
        return areRectsEqual(prev, next) ? prev : next;
      });
    }

    function scheduleSettle() {
      window.clearTimeout(settleTimeout);
      settleTimeout = window.setTimeout(commit, VIEWPORT_SETTLE_MS);
    }

    // Land the correct rect immediately on mount, then only after the viewport settles.
    commit();
    const vv = window.visualViewport;
    vv?.addEventListener("resize", scheduleSettle);
    vv?.addEventListener("scroll", scheduleSettle);
    window.addEventListener("resize", scheduleSettle);
    return () => {
      window.clearTimeout(settleTimeout);
      vv?.removeEventListener("resize", scheduleSettle);
      vv?.removeEventListener("scroll", scheduleSettle);
      window.removeEventListener("resize", scheduleSettle);
    };
  }, []);

  return boundary;
}
